import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { holdingService } from '@/api/domains/catalog/holding/holding.service';
import { areasCatalogService } from '@/api/domains/catalog/areas/areas.service';
import { costCentersCatalogService } from '@/api/domains/catalog/costCenters/costCenters.service';
import { locationsCatalogService } from '@/api/domains/catalog/locations/locations.service';
import { contractCatalogService } from '@/api/domains/catalog/contract/contract.service';
import {
  mapMonitoringFiltersToRequest,
  type GetMonitoringFiltersParams,
  type LC_MonitoringFiltersIn,
} from '@/api/domains/attendance/monitoring';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { toStringArray } from '@/utils';

import { useMonitoringSecurityGroups } from './useMonitoringSecurityGroups';


// Sentinel values for the "Modalidad horario" single-select. Per Figma this filter is one
// dropdown whose two "Todos" variants drive the includeWithoutSchedule flag, while picking a
// concrete modality scopes contractModalityIds (and forces includeWithoutSchedule off).
export const MODALITY_ALL = '__all__';
export const MODALITY_ALL_EXCLUDE_NO_SCHEDULE = '__all_exclude_no_schedule__';

// Local (non-Redux) state backing the monitoring filters panel. Dimensional filters
// hold the selected option values as string[] ([] === "Todos"); the two behavioural
// flags default to false (see below).
export interface MonitoringFilterState {
  companyIds: string[];
  areaIds: string[];
  costCenterIds: string[];
  locationIds: string[];
  groupIds: string[];
  contractModalityIds: string[];
  // "Incluir colaboradores sin horario" behaviour. OFF (default) => the "Todos (excluir sin
  // horarios)" variant; ON => "Todos" including employees without a schedule (Art. 22).
  includeWithoutSchedule: boolean;
  // "Restricción horario" (Sí/No). Default No (false).
  scheduleRestriction: boolean;
}

const DEFAULT_FILTER_STATE: MonitoringFilterState = {
  companyIds: [],
  areaIds: [],
  costCenterIds: [],
  locationIds: [],
  groupIds: [],
  contractModalityIds: [],
  includeWithoutSchedule: false,
  scheduleRestriction: false,
};

type SelectOption = { value: string; label: string };

export interface UseMonitoringFiltersResult {
  filters: MonitoringFilterState;
  catalogOptions: {
    companyOptions: SelectOption[];
    areaOptions: SelectOption[];
    costCenterOptions: SelectOption[];
    locationOptions: SelectOption[];
    securityGroupOptions: SelectOption[];
  };
  // Assembled "Modalidad horario" dropdown options: the two "Todos" sentinels followed by the
  // catalog modalities. Labels are already translated so the panel just renders them.
  modalityOptions: SelectOption[];
  // Assembled "Restricción horario" dropdown options (Sí / No), labels already translated.
  scheduleRestrictionOptions: SelectOption[];
  // Selected value of the "Modalidad horario" dropdown (a sentinel or a modality id).
  modalityValue: string;
  // Selected value of the "Restricción horario" dropdown ('true' | 'false').
  scheduleRestrictionValue: string;
  handlers: {
    onCompanyChange: (ids: string | string[]) => void;
    onAreaChange: (ids: string | string[]) => void;
    onCostCenterChange: (ids: string | string[]) => void;
    onLocationChange: (ids: string | string[]) => void;
    onSecurityGroupChange: (ids: string | string[]) => void;
    onModalityChange: (value: string | string[]) => void;
    onScheduleRestrictionChange: (value: string | string[]) => void;
  };
  // Assembles the transport payload (CSV strings + flags) from the current state.
  buildRequest: () => LC_MonitoringFiltersIn;
}

function toNumbers(ids: string[]): number[] {
  return ids.map(Number).filter((n) => !Number.isNaN(n));
}

export function useMonitoringFilters(): UseMonitoringFiltersResult {
  const { t: translate } = useTranslation();
  const [filters, setFilters] = useState<MonitoringFilterState>(DEFAULT_FILTER_STATE);

  // Dimensional catalogs load at holding scope so the monitoring panel can span every
  // company in the holding, not just the authenticated user's company.
  const companyCatalog = useCatalogLoader(
    () => holdingService.list(),
    (company) => ({ value: String(company.id), label: company.name }),
  );

  const areaCatalog = useCatalogLoader(
    (signal) => areasCatalogService.getAreas({ scope: 'holding' }, signal),
    (area) => ({ value: String(area.id), label: area.name }),
  );

  const costCenterCatalog = useCatalogLoader(
    (signal) => costCentersCatalogService.getCostCenters({ scope: 'holding' }, signal),
    (costCenter) => ({ value: String(costCenter.id), label: costCenter.name }),
  );

  const locationCatalog = useCatalogLoader(
    (signal) => locationsCatalogService.getLocations(signal, { scope: 'holding' }),
    (location) => ({ value: String(location.id), label: location.name }),
  );

  const securityGroupCatalog = useMonitoringSecurityGroups();

  const contractModalityCatalog = useCatalogLoader(
    () => contractCatalogService.getModalities(),
    (modality) => ({ value: String(modality.id), label: modality.name }),
  );

  const onCompanyChange = useCallback((ids: string | string[]) => {
    setFilters((previous) => ({ ...previous, companyIds: toStringArray(ids) }));
  }, []);

  const onAreaChange = useCallback((ids: string | string[]) => {
    setFilters((previous) => ({ ...previous, areaIds: toStringArray(ids) }));
  }, []);

  const onCostCenterChange = useCallback((ids: string | string[]) => {
    setFilters((previous) => ({ ...previous, costCenterIds: toStringArray(ids) }));
  }, []);

  const onLocationChange = useCallback((ids: string | string[]) => {
    setFilters((previous) => ({ ...previous, locationIds: toStringArray(ids) }));
  }, []);

  const onSecurityGroupChange = useCallback((ids: string | string[]) => {
    setFilters((previous) => ({ ...previous, groupIds: toStringArray(ids) }));
  }, []);

  // Single-select "Modalidad horario": the two "Todos" sentinels only toggle
  // includeWithoutSchedule; any concrete modality scopes contractModalityIds and, as a
  // targeted selection, forces includeWithoutSchedule off.
  const onModalityChange = useCallback((value: string | string[]) => {
    const selected = Array.isArray(value) ? value[0] : value;
    setFilters((previous) => {
      if (selected === MODALITY_ALL) {
        return { ...previous, contractModalityIds: [], includeWithoutSchedule: true };
      }
      if (selected === MODALITY_ALL_EXCLUDE_NO_SCHEDULE || !selected) {
        return { ...previous, contractModalityIds: [], includeWithoutSchedule: false };
      }
      return { ...previous, contractModalityIds: [selected], includeWithoutSchedule: false };
    });
  }, []);

  const onScheduleRestrictionChange = useCallback((value: string | string[]) => {
    const selected = Array.isArray(value) ? value[0] : value;
    setFilters((previous) => ({ ...previous, scheduleRestriction: selected === 'true' }));
  }, []);

  // Options for the single-select "Modalidad horario": the two "Todos" sentinels followed by
  // the catalog modalities. Owned here (not in the panel) so the component stays presentational.
  const modalityOptions = useMemo<SelectOption[]>(
    () => [
      { value: MODALITY_ALL, label: translate('attendance:monitoring.filters.modalityAll') },
      {
        value: MODALITY_ALL_EXCLUDE_NO_SCHEDULE,
        label: translate('attendance:monitoring.filters.modalityAllExcludeNoSchedule'),
      },
      ...contractModalityCatalog.options,
    ],
    [translate, contractModalityCatalog.options],
  );

  const scheduleRestrictionOptions = useMemo<SelectOption[]>(
    () => [
      { value: 'true', label: translate('attendance:monitoring.filters.yes') },
      { value: 'false', label: translate('attendance:monitoring.filters.no') },
    ],
    [translate],
  );

  const modalityValue = filters.contractModalityIds.length > 0
    ? filters.contractModalityIds[0]
    : (filters.includeWithoutSchedule ? MODALITY_ALL : MODALITY_ALL_EXCLUDE_NO_SCHEDULE);

  const scheduleRestrictionValue = filters.scheduleRestriction ? 'true' : 'false';

  const buildRequest = useCallback((): LC_MonitoringFiltersIn => {
    const params: GetMonitoringFiltersParams = {
      companyIds: toNumbers(filters.companyIds),
      areaIds: toNumbers(filters.areaIds),
      costCenterIds: toNumbers(filters.costCenterIds),
      locationIds: toNumbers(filters.locationIds),
      groupIds: toNumbers(filters.groupIds),
      contractModalityIds: toNumbers(filters.contractModalityIds),
      // Specific modalities take precedence: when any is selected, "incluir sin horario"
      // is forced off so the request carries only the chosen modality IDs.
      includeWithoutSchedule:
        filters.contractModalityIds.length > 0 ? false : filters.includeWithoutSchedule,
      scheduleRestriction: filters.scheduleRestriction,
    };
    return mapMonitoringFiltersToRequest(params);
  }, [filters]);

  return {
    filters,
    catalogOptions: {
      companyOptions: companyCatalog.options,
      areaOptions: areaCatalog.options,
      costCenterOptions: costCenterCatalog.options,
      locationOptions: locationCatalog.options,
      securityGroupOptions: securityGroupCatalog.options,
    },
    modalityOptions,
    scheduleRestrictionOptions,
    modalityValue,
    scheduleRestrictionValue,
    handlers: {
      onCompanyChange,
      onAreaChange,
      onCostCenterChange,
      onLocationChange,
      onSecurityGroupChange,
      onModalityChange,
      onScheduleRestrictionChange,
    },
    buildRequest,
  };
}

export default useMonitoringFilters;
